/** NotificationsProvider — глобальный source-of-truth для уведомлений.
 *
 *  Связывает TanStack Query (REST) и WebSocket push:
 *  * `createQuery` тянет список и summary по REST для активной локации/скоупа.
 *  * `NotificationsSocket` подписывается на WS; `notification.new`/`.resolved`
 *    точечно обновляют кеш TanStack без перефетча.
 *  * Polling-fallback (interval 30s) включается, если WS не в state `open`
 *    дольше 5 сек — на случай разрыва соединения.
 */
import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  useContext,
  type Accessor,
  type ParentComponent,
} from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { getEnv } from "~/lib/env";
import { getToken } from "~/state/session";
import { useAuth } from "~/lib/auth";
import { useWorkspace } from "~/lib/workspace";
import {
  fetchNotifications,
  fetchSummary,
  markRead as apiMarkRead,
  markAllRead as apiMarkAllRead,
  type Notification,
  type NotificationScope,
  type NotificationsListResponse,
  type NotificationsSummary,
} from "~/lib/notifications";
import { NotificationsSocket, type WsState } from "~/lib/notificationsSocket";

interface NotificationsContextValue {
  /** Текущий выбранный скоуп (active=только активная локация, all=все доступные). */
  scope: Accessor<NotificationScope>;
  setScope: (s: NotificationScope) => void;
  /** Показывать ли уже обработанные (resolved) уведомления — для архивного вида. */
  showResolved: Accessor<boolean>;
  setShowResolved: (v: boolean) => void;
  /** Список уведомлений для текущего скоупа. */
  items: Accessor<Notification[]>;
  /** Счётчики unread (рассчитываются на бэке). */
  summary: Accessor<NotificationsSummary | undefined>;
  /** Состояние WS-соединения для индикатора на колокольчике. */
  wsState: Accessor<WsState>;
  /**
   * WS деградировал: не в состоянии `open` дольше грейс-периода, включён
   * polling-fallback. Дебаунснут — короткие реконнекты (<5с) сюда не попадают,
   * чтобы индикатор «Связь потеряна · polling» не мигал на каждом блипе.
   */
  pollingActive: Accessor<boolean>;
  /** Дёрнуть REST mark-read у одного и оптимистично обновить кеш. */
  markRead: (id: number) => Promise<void>;
  /** Mark-all-read в текущем scope + ручной refetch. */
  markAllRead: () => Promise<void>;
  /** Принудительный рефетч списка (после inline-action). */
  refetch: () => Promise<unknown>;
  /** Loading / error для UI. */
  isLoading: Accessor<boolean>;
  isError: Accessor<boolean>;
}

const NotificationsContext = createContext<NotificationsContextValue>();

const SCOPE_STORAGE_KEY = "ritm.notifications_scope";
const SHOW_RESOLVED_STORAGE_KEY = "ritm.notifications_show_resolved";

// Частота фонового опроса уведомлений (счётчик + список). WebSocket даёт
// мгновенный push, но при перебоях Redis он может «молчать», оставаясь открытым,
// поэтому держим частый фоновый опрос как надёжный резерв — чтобы колокольчик
// обновлялся почти мгновенно даже без рабочего WS. Опрос автоматически ставится
// на паузу, когда вкладка неактивна (поведение TanStack Query), и догоняется при
// возврате на вкладку (refetchOnWindowFocus), поэтому лишней нагрузки в фоне нет.
const NOTIF_POLL_INTERVAL_MS = 10_000;

function readInitialScope(): NotificationScope {
  try {
    const raw = localStorage.getItem(SCOPE_STORAGE_KEY);
    if (raw === "active" || raw === "all") return raw;
  } catch {
    /* private mode */
  }
  return "active";
}

function readInitialShowResolved(): boolean {
  try {
    return localStorage.getItem(SHOW_RESOLVED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export const NotificationsProvider: ParentComponent = (props) => {
  const queryClient = useQueryClient();
  const { meQuery } = useAuth();
  const { activeLocation } = useWorkspace();

  const [scope, setScopeSignal] = createSignal<NotificationScope>(readInitialScope());
  const [showResolved, setShowResolvedSignal] = createSignal<boolean>(readInitialShowResolved());
  const [wsState, setWsState] = createSignal<WsState>("idle");

  const setScope = (s: NotificationScope) => {
    try {
      localStorage.setItem(SCOPE_STORAGE_KEY, s);
    } catch {
      /* private mode */
    }
    setScopeSignal(s);
  };

  const setShowResolved = (v: boolean) => {
    try {
      localStorage.setItem(SHOW_RESOLVED_STORAGE_KEY, v ? "1" : "0");
    } catch {
      /* private mode */
    }
    setShowResolvedSignal(v);
  };

  const locationId = createMemo(() => activeLocation()?.id ?? null);
  const isAuthed = createMemo(() => meQuery.data?.user != null);

  // Polling-интервал реактивно включается, если WS не open > 5 сек.
  const [pollingEnabled, setPollingEnabled] = createSignal(false);
  let pollingTimer: number | null = null;
  createEffect(() => {
    const s = wsState();
    if (pollingTimer !== null) {
      window.clearTimeout(pollingTimer);
      pollingTimer = null;
    }
    if (s === "open") {
      setPollingEnabled(false);
    } else {
      pollingTimer = window.setTimeout(() => setPollingEnabled(true), 5_000);
    }
  });
  onCleanup(() => {
    if (pollingTimer !== null) {
      window.clearTimeout(pollingTimer);
      pollingTimer = null;
    }
  });

  const listQuery = createQuery<NotificationsListResponse>(() => ({
    queryKey: ["notifications", "list", scope(), locationId(), showResolved()],
    enabled: isAuthed(),
    queryFn: () =>
      fetchNotifications({
        scope: scope(),
        locationId: locationId(),
        limit: 50,
        includeResolved: showResolved(),
      }),
    // Список тоже опрашиваем на фоне всегда (см. коммент к summaryQuery ниже):
    // иначе счётчик обновится, а содержимое выпадашки останется устаревшим и
    // пользователь при открытии не увидит новое уведомление.
    refetchInterval: NOTIF_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    // Фоновый поллинг: разрыв сети — это норма, не нужно нудеть тостом.
    meta: { skipGlobalError: true },
  }));

  // Summary (число на колокольчике) опрашиваем на фоне ВСЕГДА, а не только при
  // деградации WS. Причина: heartbeat (ping/pong) проверяет живость сокета, но
  // НЕ живость доставки — сообщения идут через Redis-группы, а ping/pong нет.
  // Если Redis отваливается, сокет остаётся `open`, push молчит, и без
  // безусловного опроса счётчик замирал бы до перезагрузки страницы. Интервал —
  // NOTIF_POLL_INTERVAL_MS (частый), чтобы колокольчик обновлялся почти
  // мгновенно даже без рабочего WS.
  const summaryQuery = createQuery<NotificationsSummary>(() => ({
    queryKey: ["notifications", "summary", locationId()],
    enabled: isAuthed(),
    queryFn: () => fetchSummary(locationId()),
    refetchInterval: NOTIF_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    meta: { skipGlobalError: true },
  }));

  // ─── WebSocket-подписка ────────────────────────────────────────────
  let socket: NotificationsSocket | null = null;

  createEffect(() => {
    // Закрываем старое соединение при логауте.
    if (!isAuthed()) {
      socket?.disconnect();
      socket = null;
      setWsState("idle");
      return;
    }
    const token = getToken();
    if (!token) return;
    if (socket) return; // уже подключён

    socket = new NotificationsSocket({
      token,
      apiBaseUrl: getEnv().apiBaseUrl,
      onMessage: (event) => {
        if (event.type === "notification.new") {
          // Точечный апдейт списка: добавляем сверху + bump summary.
          queryClient.setQueryData<NotificationsListResponse | undefined>(
            ["notifications", "list", scope(), locationId(), showResolved()],
            (prev) => {
              if (!prev) return prev;
              if (prev.items.some((n) => n.id === event.notification.id)) return prev;
              return { ...prev, items: [event.notification, ...prev.items] };
            },
          );
          void queryClient.invalidateQueries({
            queryKey: ["notifications", "summary"],
          });
        }
        if (event.type === "notification.resolved") {
          queryClient.setQueryData<NotificationsListResponse | undefined>(
            ["notifications", "list", scope(), locationId(), showResolved()],
            (prev) => {
              if (!prev) return prev;
              if (showResolved()) {
                // В режиме архива оставляем элемент, помечаем resolved_at.
                const now = new Date().toISOString();
                return {
                  ...prev,
                  items: prev.items.map((n) =>
                    n.id === event.id && !n.resolved_at ? { ...n, resolved_at: now } : n,
                  ),
                };
              }
              return { ...prev, items: prev.items.filter((n) => n.id !== event.id) };
            },
          );
          void queryClient.invalidateQueries({
            queryKey: ["notifications", "summary"],
          });
        }
      },
      onStateChange: setWsState,
    });
    socket.connect();
  });

  onCleanup(() => {
    socket?.disconnect();
    socket = null;
  });

  // ─── Mutations ─────────────────────────────────────────────────────
  const markRead = async (id: number) => {
    // Оптимистично выставляем is_read=true в кеше.
    queryClient.setQueryData<NotificationsListResponse | undefined>(
      ["notifications", "list", scope(), locationId(), showResolved()],
      (prev) =>
        prev
          ? { ...prev, items: prev.items.map((n) => (n.id === id ? { ...n, is_read: true } : n)) }
          : prev,
    );
    try {
      await apiMarkRead(id);
    } finally {
      void queryClient.invalidateQueries({ queryKey: ["notifications", "summary"] });
    }
  };

  const markAllRead = async () => {
    try {
      await apiMarkAllRead(scope(), locationId());
    } finally {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notifications", "list"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications", "summary"] }),
      ]);
    }
  };

  const refetch = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["notifications", "list"] }),
      queryClient.invalidateQueries({ queryKey: ["notifications", "summary"] }),
    ]);

  const items = createMemo<Notification[]>(() => listQuery.data?.items ?? []);
  const summary = createMemo<NotificationsSummary | undefined>(() => summaryQuery.data);
  const isLoading = createMemo(() => listQuery.isLoading);
  const isError = createMemo(() => listQuery.isError);

  return (
    <NotificationsContext.Provider
      value={{
        scope,
        setScope,
        showResolved,
        setShowResolved,
        items,
        summary,
        wsState,
        pollingActive: pollingEnabled,
        markRead,
        markAllRead,
        refetch,
        isLoading,
        isError,
      }}
    >
      {props.children}
    </NotificationsContext.Provider>
  );
};

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}
